import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../components/AuthProvider';
import { showTemporaryMessage } from "../components/Message";
import LanguageSwitcher from '../components/LanguageSwitcher';
import logoicon from '../images/tuutuum-logo-icon.png';

import {
	useNavigate
} from "react-router-dom";

import {
	validate_name
} from "../tools";

import { get_bearers } from "../tools-network";

const CreateBearerStep1 = ({onNext, onCancel, name, setName, isVerbal, setIsVerbal}) => {
	const { t } = useTranslation();

	const { userType, loginToken, userId } = React.useContext(AuthContext);
	const [existingBearers, setExistingBearers] = useState([]);
	const [nameWarning, setNameWarning] = useState('');

	// Fetch existing bearers for duplicate check
	useEffect(() => {
		const fetchBearers = async () => {
			try {
				const res = await get_bearers(loginToken, userId);
				if (res.status && res.data) {
					// Convert {id: name} object to array of names
					const bearerNames = Object.values(res.data);
					setExistingBearers(bearerNames);
				}
			} catch (err) {
				console.log("Error fetching bearers: " + err);
			}
		};
		fetchBearers();
	}, [loginToken, userId]);

	// Check for duplicate name
	useEffect(() => {
		if (name.trim() && existingBearers.length > 0) {
			const isDuplicate = existingBearers.some(
				existingName => existingName.toLowerCase() === name.trim().toLowerCase()
			);
			if (isDuplicate) {
				setNameWarning(t("bearer_name_already_exists"));
			} else {
				setNameWarning('');
			}
		} else {
			setNameWarning('');
		}
	}, [name, existingBearers, t]);
	
	const onNextValidate = () => {
		if(!validate_name(name)) {
			showTemporaryMessage(t("name_is_not_valid"), t("name_contains_invalid_characters"), 'warning');
			return;
		}
		if (nameWarning) {
			showTemporaryMessage(t("bearer_name_already_exists"), t("please_choose_different_name"), 'warning');
			return;
		}
		onNext();
	};

	return(<>
            <div className="flex items-center justify-center h-full w-full bg-gradient-to-r from-cyan-100 to-green-100">
				<div className="relative flex flex-col neumorp items-center justify-center space-y-5 bg-white px-6 py-10 sm:p-10 rounded-lg shadow-lg w-full max-w-xs sm:max-w-sm">
					{/* Language Switcher - positioned absolutely in top right */}
					<div className="absolute top-4 right-4">
						<LanguageSwitcher />
					</div>
					{/* Logo */}
					<img src={logoicon} alt="Tuutuum Logo" className="block h-7 w-auto"/>
					<h2 className="text-center text-2xl leading-tight px-2">
						{t("set_name_of_bearer")}
					</h2>
					{/* Input name */}
					<div className="flex flex-col w-full gap-1 justify-stretch pt-1">
						<input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Alice"
							className={`block rounded-md border w-full px-2 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring focus:ring-cyan-600 focus:border-cyan-600 placeholder-gray-400 sm:text-sm sm:leading-4 ${
								nameWarning ? 'border-yellow-500' : 'border-gray-300'
							}`}
						/>
						{nameWarning && (
							<p className="text-xs text-yellow-600 mt-1">
								{nameWarning}
							</p>
						)}
					</div>
					{/* Is Verbal - checkbox (checked = can't talk) */}
					<div className="w-full pt-2">
						<label htmlFor="person-cannot-talk" className="flex items-center cursor-pointer">
							<input
								type="checkbox"
								id="person-cannot-talk"
								checked={!isVerbal}
								onChange={(e) => setIsVerbal(!e.target.checked)}
								className="mr-2 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
							/>
							<span className="text-sm text-gray-900">{t("person_cannot_talk")}</span>
						</label>
					</div>
					{/* Button */}
					<div className="flex flex-nowrap w-full gap-4 justify-between">
						{onCancel && (
							<button
								onClick={onCancel}
								className="btn-main btn-main:hover text-white px-3 py-1 w-auto rounded-md sm:leading-6 text-center font-semibold text-sm transition ease-in-out hover:scale-105"
							>
								{t("previous")}
							</button>
						)}
						<button
							onClick={onNextValidate}
							disabled={name == '' || nameWarning !== ''}
							className="btn-main btn-main:hover text-white px-3 py-1 w-auto rounded-md sm:leading-6 text-center font-semibold text-sm transition ease-in-out hover:scale-105 disabled:bg-gray-400 disabled:text-grey-600 disabled:cursor-not-allowed"
						>
							{t("next")}
						</button>
					</div>
				</div>
			</div>
		</>);
}

export default CreateBearerStep1;
